import React, { useEffect, useMemo, useState } from 'react';
import { loadClubBranding } from '../clubBranding';
import { loadAppSettings } from '../appSettings';
import wingitDesign from '../designSystem';

export default function DashboardHero({
  isMobile,
  user,
  userMeta,
  organisationId,
  profileData,
  membershipTier,
  handleLogout,
  onBookAircraft,
}) {
  const [imageIndex, setImageIndex] = useState(0);
  const [clubBranding, setClubBranding] = useState({
    clubName: '',
    logoUrl: '',
    heroImages: [],
  });

  useEffect(() => {
    let cancelled = false;

    async function fetchBranding() {
      const brandingData =
        await loadClubBranding(
          organisationId
        );
      if (!cancelled) setClubBranding(brandingData);
    }

    fetchBranding();

    return () => {
      cancelled = true;
    };
  }, [organisationId]);

  const appSettings = useMemo(
    () => loadAppSettings(organisationId),
    [organisationId]
  );
  const branding = appSettings?.branding || {};

  const clubName =
    profileData?.orgName ||
    userMeta?.org_name ||
    clubBranding.clubName ||
    branding?.clubName ||
    'Your Flying Club';

  const fullName = String(
    profileData?.fullName ||
      userMeta?.full_name ||
      ''
  ).trim();

  const displayName = getFirstName(fullName);

  const heroImages = clubBranding.heroImages?.length > 0
    ? clubBranding.heroImages
    : [];

  const clubLogoUrl = clubBranding.logoUrl || branding?.logoUrl || '';
  const clubInitials = getClubInitials(clubName);

  useEffect(() => {
    if (heroImages.length <= 1) return undefined;

    const timer = setInterval(() => {
      setImageIndex((prev) => (prev + 1) % heroImages.length);
    }, 15000);

    return () => clearInterval(timer);
  }, [heroImages.length]);

  const greeting = getGreeting();

  return (
    <section
      style={{
        ...heroStyle,
        minHeight: isMobile ? 230 : 320,
        padding: isMobile ? '1rem' : '1.45rem',
        borderRadius: isMobile ? 20 : 28,
      }}
    >
      {heroImages.length > 0 ? (
        <div style={heroImageLayerStyle}>
          {heroImages.map((imageUrl, index) => (
            <img
              key={`${imageUrl}-${index}`}
              src={imageUrl}
              alt=""
              aria-hidden="true"
              style={{
                ...heroImageStyle,
                opacity: index === imageIndex ? 1 : 0,
                transform: index === imageIndex ? 'scale(1)' : 'scale(1.045)',
              }}
            />
          ))}
        </div>
      ) : (
        <div style={fallbackBackgroundStyle} />
      )}

      <div style={heroTextureStyle} />
      <div style={heroOverlayStyle} />

      <div style={topRowStyle}>
        <div />

        <div style={logoutWrapStyle}>
          <button type="button" onClick={handleLogout} style={logoutButtonStyle}>
            Log out
          </button>
        </div>
      </div>

      <div
        style={{
          ...contentStyle,
          minHeight: isMobile ? 155 : 205,
          alignItems: isMobile ? 'flex-end' : 'center',
        }}
      >
        <div style={{ maxWidth: isMobile ? '100%' : 760 }}>
          <div style={eyebrowStyle}>Dashboard</div>

          <div
            style={{
              ...clubIdentityRowStyle,
              alignItems: isMobile ? 'center' : 'flex-start',
            }}
          >
            <div
              style={{
                ...clubLogoFrameStyle,
                width: isMobile ? 78 : 104,
                height: isMobile ? 78 : 104,
                borderRadius: 999,
                padding: clubLogoUrl ? (isMobile ? 10 : 12) : 0,
              }}
            >
              {clubLogoUrl ? (
                <img src={clubLogoUrl} alt={clubName} style={clubLogoStyle} />
              ) : (
                <div style={clubInitialsStyle}>{clubInitials}</div>
              )}
            </div>

            <div style={clubIdentityTextStyle}>
              <div
                style={{
                  ...clubNameStyle,
                  fontSize: isMobile ? '1.45rem' : '2rem',
                  marginTop: 0,
                }}
              >
                {clubName}
              </div>

              <div style={clubTaglineStyle}>PLAN • FLY • LOG</div>

              <div style={poweredByStyle}>
                <img src="/logo192.png" alt="Wingit" style={poweredByLogoStyle} />
                <span>Powered by Wingit</span>
              </div>
            </div>
          </div>

          <h1
            style={{
              ...greetingStyle,
              fontSize: isMobile ? '1.22rem' : '1.65rem',
            }}
          >
            {greeting}, {displayName}
          </h1>

          <div style={metaRowStyle}>
            <button
              type="button"
              onClick={() => onBookAircraft()}
              style={bookAircraftButtonStyle}
            >
              Book Aircraft
            </button>
            <span style={metaMembershipStyle}>{membershipTier || 'Calendar Only'}</span>
            {user?.email && <span style={metaTextStyle}>{user.email}</span>}
          </div>
        </div>
      </div>

      {heroImages.length > 1 && (
        <div style={dotsStyle}>
          {heroImages.map((_, index) => (
            <span
              key={index}
              style={{
                ...dotStyle,
                width: index === imageIndex ? 18 : 7,
                background: index === imageIndex
                  ? wingitDesign.colors.primary
                  : 'rgba(255,255,255,0.32)',
              }}
            />
          ))}
        </div>
      )}
    </section>
  );
}

function getGreeting() {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 18) return 'Good afternoon';
  return 'Good evening';
}

function getFirstName(fullName) {
  const parts = String(fullName || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  return parts[0] || 'Pilot';
}

function getClubInitials(clubName) {
  const words = String(clubName || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  if (words.length === 0) {
    return 'WC';
  }

  return words
    .slice(0, 2)
    .map((word) => word.charAt(0).toUpperCase())
    .join('');
}

const heroStyle = {
  position: 'relative',
  overflow: 'hidden',
  color: wingitDesign.colors.text,
  border: '1px solid rgba(148,198,235,0.22)',
  boxShadow: '0 24px 70px rgba(0,0,0,0.32)',
  background: wingitDesign.colors.surfaceElevated,
  marginBottom: '1.2rem',
};

const heroImageLayerStyle = {
  position: 'absolute',
  inset: 0,
  zIndex: 0,
};

const heroImageStyle = {
  position: 'absolute',
  inset: 0,
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  objectPosition: 'center center',
  transition: 'opacity 1800ms ease-in-out, transform 15000ms ease-in-out',
  willChange: 'opacity, transform',
};

const fallbackBackgroundStyle = {
  position: 'absolute',
  inset: 0,
  background: 'linear-gradient(135deg, #101b2f 0%, #18304f 48%, #081321 100%)',
  zIndex: 0,
};

const heroTextureStyle = {
  position: 'absolute',
  inset: 0,
  backgroundImage: `
    linear-gradient(rgba(255,255,255,0.035) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255,255,255,0.028) 1px, transparent 1px)
  `,
  backgroundSize: '48px 48px',
  opacity: 0.26,
  zIndex: 1,
};

const heroOverlayStyle = {
  position: 'absolute',
  inset: 0,
  background: `
    radial-gradient(circle at 78% 18%, rgba(85,199,255,0.17), transparent 30%),
    linear-gradient(90deg, rgba(5,14,27,0.96) 0%, rgba(5,14,27,0.76) 45%, rgba(5,14,27,0.38) 100%),
    linear-gradient(0deg, rgba(5,14,27,0.90) 0%, rgba(5,14,27,0.18) 65%)
  `,
  zIndex: 2,
};

const topRowStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: '1rem',
  position: 'relative',
  zIndex: 4,
};

const logoutWrapStyle = {
  display: 'flex',
  justifyContent: 'flex-end',
};

const clubIdentityRowStyle = {
  display: 'flex',
  gap: '1rem',
  flexWrap: 'wrap',
  marginBottom: '1rem',
};

const clubIdentityTextStyle = {
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  minWidth: 0,
};

const clubLogoFrameStyle = {
  background: 'rgba(12,24,38,0.72)',
  border: '1px solid rgba(255,255,255,0.22)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  boxShadow: '0 14px 34px rgba(0,0,0,0.28)',
  overflow: 'hidden',
};

const clubLogoStyle = {
  width: '100%',
  height: '100%',
  objectFit: 'contain',
};

const clubInitialsStyle = {
  color: 'rgba(235,245,255,0.96)',
  fontSize: '1.7rem',
  fontWeight: 800,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
};

const clubTaglineStyle = {
  marginTop: '0.35rem',
  color: 'rgba(215,235,255,0.84)',
  fontSize: '0.78rem',
  fontWeight: 780,
  letterSpacing: '0.22em',
  textTransform: 'uppercase',
};

const poweredByStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '0.38rem',
  marginTop: '0.5rem',
  color: 'rgba(215,235,255,0.62)',
  fontSize: '0.72rem',
  fontWeight: 650,
  letterSpacing: '0.03em',
};

const poweredByLogoStyle = {
  width: 14,
  height: 14,
  objectFit: 'contain',
  opacity: 0.82,
  filter: 'drop-shadow(0 4px 10px rgba(0,0,0,0.28))',
};

const logoutButtonStyle = {
  ...wingitDesign.buttons.ghost,
  background: 'rgba(255,255,255,0.08)',
  color: '#d7ebff',
  border: '1px solid rgba(255,255,255,0.16)',
  padding: '0.48rem 0.78rem',
  fontSize: '0.78rem',
};

const contentStyle = {
  position: 'relative',
  zIndex: 3,
  display: 'flex',
  justifyContent: 'space-between',
  gap: '1.5rem',
};

const eyebrowStyle = {
  color: wingitDesign.colors.primary,
  fontSize: '0.72rem',
  fontWeight: 850,
  letterSpacing: '0.16em',
  textTransform: 'uppercase',
  marginBottom: '0.65rem',
};

const greetingStyle = {
  margin: '0.15rem 0 0',
  color: wingitDesign.colors.text,
  fontWeight: 850,
  lineHeight: 1.08,
  textShadow: '0 10px 30px rgba(0,0,0,0.45)',
};

const clubNameStyle = {
  marginTop: '0.75rem',
  color: 'rgba(215,235,255,0.88)',
  fontWeight: 650,
  lineHeight: 1.18,
  textShadow: '0 8px 24px rgba(0,0,0,0.38)',
};

const metaRowStyle = {
  display: 'flex',
  gap: '0.55rem',
  flexWrap: 'wrap',
  alignItems: 'center',
  marginTop: '1.05rem',
};

const bookAircraftButtonStyle = {
  ...wingitDesign.buttons.primary,
  borderRadius: 999,
  padding: '0.48rem 0.9rem',
  fontSize: '0.8rem',
  fontWeight: 800,
};

const metaMembershipStyle = {
  color: 'rgba(215,235,255,0.72)',
  fontSize: '0.8rem',
  fontWeight: 700,
};

const metaTextStyle = {
  color: 'rgba(215,235,255,0.68)',
  fontSize: '0.82rem',
};

const dotsStyle = {
  position: 'absolute',
  left: '50%',
  bottom: 16,
  transform: 'translateX(-50%)',
  display: 'flex',
  gap: 8,
  zIndex: 4,
};

const dotStyle = {
  height: 7,
  borderRadius: 999,
  transition: wingitDesign.transition.normal,
};